/**
 * Traffic and surface-state estimation from consecutive frames of one camera.
 *
 * Same contract as the haze module: real measurements on the real pixels the
 * operator is looking at, no model, no network, ~O(pixels).
 *
 * WHAT IS ACTUALLY MEASURED
 *  - `motion`: mean absolute luma difference against the previous frame of the
 *    SAME camera. A parked street and a moving one differ here and nowhere else.
 *  - `changedFraction`: share of pixels whose difference clears a noise floor.
 *    Separates "one truck went past" from "wind in the trees", which move few
 *    pixels; "everything is moving" is a different failure.
 *  - `blobs`: 4-connected components of the change mask, i.e. a count of
 *    *separately moving things*, with their areas. This is a motion-blob count,
 *    NOT a vehicle classification — it cannot tell a car from a motorbike from a
 *    person, and the panel says "moving objects", never "vehicles".
 *  - `glare`: bright low-saturation highlights, the signature of headlights and
 *    of standing water reflecting a lamp. Both matter here: one is traffic, the
 *    other is a wet road. Deliberately reported as one ambiguous number rather
 *    than guessed apart.
 *
 * THE HONEST LIMIT: a single still pair cannot measure speed or density. Two
 * frames give displacement only if you know the ground scale, and a public
 * camera gives you neither the focal length nor the mounting height. Anything
 * claiming km/h from this would be inventing a calibration. So the engine
 * reports motion and count, and refuses to report speed.
 */

import type { Rgb } from "./haze";

export interface Blob {
  x: number;
  y: number;
  w: number;
  h: number;
  areaPx: number;
}

export interface TrafficResult {
  /** 0–100 motion energy. */
  motion: number;
  /** Share of pixels that changed, 0–1. */
  changedFraction: number;
  /** Separately-moving regions, largest first. */
  blobs: Blob[];
  /** Bright, desaturated highlight fraction, 0–1. */
  glare: number;
  /** Mean absolute luma delta, 0–1. */
  meanDelta: number;
}

/** Luma plane at working resolution. */
function lumaPlane(rgb: Rgb): Float32Array {
  const { data, width: w, height: h } = rgb;
  const out = new Float32Array(w * h);
  for (let i = 0; i < w * h; i++) {
    out[i] =
      (0.2126 * data[i * 4] + 0.7152 * data[i * 4 + 1] + 0.0722 * data[i * 4 + 2]) / 255;
  }
  return out;
}

/**
 * Difference two consecutive frames of the same camera.
 * Returns null if the frames are not the same size — a resized tile would
 * produce a meaningless diff that looks like a scene change.
 */
export function diffFrames(prev: Rgb, cur: Rgb): TrafficResult | null {
  if (prev.width !== cur.width || prev.height !== cur.height) return null;
  const { width: w, height: h } = cur;
  const n = w * h;
  if (n === 0) return null;

  const a = lumaPlane(prev);
  const b = lumaPlane(cur);

  // Noise floor. Below this it is sensor noise and JPEG ringing, not motion.
  const NOISE = 0.06;
  const MIN_AREA = 4; // px at working resolution ≈ a motorcycle, not a bird

  const mask = new Uint8Array(n);
  let sum = 0;
  let changed = 0;
  for (let i = 0; i < n; i++) {
    const d = Math.abs(a[i] - b[i]);
    sum += d;
    if (d > NOISE) {
      mask[i] = 1;
      changed++;
    }
  }
  const meanDelta = sum / n;
  const changedFraction = changed / n;

  const blobs = connectedComponents(mask, w, h, MIN_AREA);

  // Glare: bright AND low-saturation, so a white wall in daylight does not
  // masquerade as headlights. Needs the colour plane, not just luma.
  let glarePx = 0;
  for (let i = 0; i < n; i++) {
    const r = cur.data[i * 4] / 255;
    const g = cur.data[i * 4 + 1] / 255;
    const bl = cur.data[i * 4 + 2] / 255;
    const mx = Math.max(r, g, bl);
    const mn = Math.min(r, g, bl);
    const sat = mx <= 0 ? 0 : (mx - mn) / mx;
    if (mx > 0.82 && sat < 0.22) glarePx++;
  }
  const glare = glarePx / n;

  const motion = Math.round(Math.max(0, Math.min(100, meanDelta * 900)));
  return {
    motion,
    changedFraction: Math.round(changedFraction * 1000) / 1000,
    blobs,
    glare: Math.round(glare * 1000) / 1000,
    meanDelta: Math.round(meanDelta * 1000) / 1000,
  };
}

/** 4-connected components over a binary mask; drops specks below minArea. */
function connectedComponents(
  mask: Uint8Array,
  w: number,
  h: number,
  minArea: number,
): Blob[] {
  const seen = new Uint8Array(w * h);
  const out: Blob[] = [];
  const stack: number[] = [];

  for (let start = 0; start < w * h; start++) {
    if (mask[start] === 0 || seen[start] === 1) continue;
    stack.length = 0;
    stack.push(start);
    seen[start] = 1;
    let minX = w;
    let maxX = 0;
    let minY = h;
    let maxY = 0;
    let area = 0;

    while (stack.length) {
      const i = stack.pop()!;
      const x = i % w;
      const y = (i / w) | 0;
      area++;
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
      if (x > 0 && mask[i - 1] === 1 && seen[i - 1] === 0) { seen[i - 1] = 1; stack.push(i - 1); }
      if (x < w - 1 && mask[i + 1] === 1 && seen[i + 1] === 0) { seen[i + 1] = 1; stack.push(i + 1); }
      if (y > 0 && mask[i - w] === 1 && seen[i - w] === 0) { seen[i - w] = 1; stack.push(i - w); }
      if (y < h - 1 && mask[i + w] === 1 && seen[i + w] === 0) { seen[i + w] = 1; stack.push(i + w); }
    }
    if (area >= minArea) {
      out.push({ x: minX, y: minY, w: maxX - minX + 1, h: maxY - minY + 1, areaPx: area });
    }
  }
  out.sort((p, q) => q.areaPx - p.areaPx);
  return out;
}

/** Operator-facing congestion read. Named for what was measured, not inferred. */
export function trafficBand(motion: number, blobs: number): {
  label: string;
  severity: "still" | "light" | "busy" | "heavy";
} {
  if (motion < 6 && blobs === 0) return { label: "no motion", severity: "still" };
  if (motion < 18) return { label: `${blobs} moving`, severity: "light" };
  if (motion < 42) return { label: `${blobs} moving · busy`, severity: "busy" };
  return { label: `${blobs} moving · heavy`, severity: "heavy" };
}

/**
 * Wet-surface signal. Bright + desaturated + high local contrast is what
 * standing water on asphalt looks like at night; it overlaps almost exactly
 * with headlight glare, so this is reported as a *candidate* surface reading
 * with the ambiguity stated, never as "the road is flooded".
 */
export function wetSurfaceCandidate(t: TrafficResult | null): boolean {
  return !!t && t.glare > 0.012 && t.changedFraction < 0.25;
}
